import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { videoTransitions } from '../config/videos';

/**
 * Custom hook for route navigation with transition videos when defined in the videoTransitions configuration
 */
export const useCustomNavigation = () => {
    const navigate = useNavigate();
    
    /**
     * Navigate to a new route, using transition videos when available
     * @param {string} to - The target route to navigate to
     */
    const navigateTo = useCallback((to) => {
        // Get the current route
        const currentPath = window.location.pathname;
        
        //console.log(`[useCustomNavigation] Navigating from ${currentPath} to ${to}`);
        
        // If navigating to home page, reset the overlay animation state
        if (to === '/') {
            sessionStorage.removeItem('animationCompleted');
            sessionStorage.removeItem('lastDisplayedCount');
            sessionStorage.setItem('overlayVisible', 'false');
        }
        
        // Check if there's a transition defined for this route change
        const transition = videoTransitions.find(t => {
            // Check if the from route matches the current path, handling wildcards
            const fromMatches = t.from.endsWith('/*') 
                ? currentPath.startsWith(t.from.slice(0, -2))
                : t.from === currentPath;
            
            // Check if the to route matches the target route, handling wildcards
            const toMatches = t.to.endsWith('/*') 
                ? to.startsWith(t.to.slice(0, -2))
                : t.to === to;
                
            //console.log(`[useCustomNavigation] Checking transition: ${t.from} -> ${t.to}`);
            //console.log(`[useCustomNavigation] fromMatches: ${fromMatches}, toMatches: ${toMatches}`);
            
            return fromMatches && toMatches;
        });
        
        //console.log(`[useCustomNavigation] Found transition:`, transition);
        
        if (transition) {
            //console.log(`[useCustomNavigation] Using transition video for navigation from ${currentPath} to ${to}`);
            //console.log(`[useCustomNavigation] Transition video: ${transition.src}`);
            
            // Create a simple approach to play the transition video
            // 1. Fade out the content
            document.body.classList.add('content-fading-out');
            
            // 2. Create a full-screen video element
            const transitionVideo = document.createElement('video');
            transitionVideo.id = 'transition-video-overlay';
            transitionVideo.style.position = 'fixed';
            transitionVideo.style.top = '0';
            transitionVideo.style.left = '0';
            transitionVideo.style.width = '100%';
            transitionVideo.style.height = '100%';
            transitionVideo.style.objectFit = 'cover';
            transitionVideo.style.zIndex = '9999'; // Lower than the menu and logo (which are 10000)
            transitionVideo.style.backgroundColor = 'black';
            transitionVideo.autoplay = true;
            transitionVideo.playsInline = true;
            
            // Disable Picture-in-Picture
            transitionVideo.disablePictureInPicture = true;
            transitionVideo.setAttribute('disablepictureinpicture', '');
            
            // Check if video should be unmuted
            const videoShouldBeUnmuted = 
                (typeof window !== 'undefined' && window.STILL_LOVED_VIDEO_UNMUTED) ||
                sessionStorage.getItem('videoUnmuted') === 'true';
            
            transitionVideo.muted = !videoShouldBeUnmuted;
            if (videoShouldBeUnmuted && window.STILL_LOVED_USER_INTERACTED) {
                transitionVideo.volume = 1.0;
                //console.log(`[useCustomNavigation] Unmuting transition video`);
            } else {
                transitionVideo.muted = true;
                //console.log(`[useCustomNavigation] Muting transition video`);
            }
            
            transitionVideo.src = transition.src;
            
            // 3. Add the video to the body
            document.body.appendChild(transitionVideo);
            
            // 4. Play the video and navigate when it ends
            transitionVideo.addEventListener('ended', () => {
                //console.log(`[useCustomNavigation] Transition video ended, navigating to: ${to}`);
                
                // Fade out the video then remove the video
                transitionVideo.classList.add('fade-out');
                setTimeout(() => {
                    document.body.removeChild(transitionVideo);
                }, 1000);
                
                // Remove the content-fading-out class
                document.body.classList.remove('content-fading-out');
                
                // Navigate to the target route
                navigate(to);
            });
            
            // 5. Handle errors
            transitionVideo.addEventListener('error', () => {
                console.error('Error playing transition video');
                
                // Remove the video
                if (document.body.contains(transitionVideo)) {
                    document.body.removeChild(transitionVideo);
                }
                
                // Remove the content-fading-out class
                document.body.classList.remove('content-fading-out');
                
                // Navigate to the target route
                navigate(to);
            });
            
            // 6. Start playing the video
            transitionVideo.load();
            transitionVideo.play().catch(err => {
                console.error('Error playing transition video:', err);
                
                // Remove the video
                if (document.body.contains(transitionVideo)) {
                    document.body.removeChild(transitionVideo);
                }
                
                // Remove the content-fading-out class
                document.body.classList.remove('content-fading-out');
                
                // Navigate to the target route
                navigate(to);
            });
        } else {
            // Fallback to regular navigation if no transition is defined
            //console.log(`[useCustomNavigation] Using regular navigation to ${to}`);
            navigate(to);
        }
    }, [navigate]);
    
    return { navigateTo };
}; 